import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import StatusChip from "components/StatusChip";
import Swal from "sweetalert2";
import { API_BASE, API_HOST } from "config/api";

// Valid permission keys — must stay in sync with config/modules.php.
// Stale DB values are silently dropped before saving to avoid 422 errors.
const VALID_PERM_KEYS = new Set([
  "members",
  "departments",
  "ministries",
  "attendance",
  "biometric",
  "payments",
  "sms",
  "reports",
  "calendar",
  "visitors",
  "new_converts",
  "small_groups",
  "pastoral_care",
  "serving_teams",
  "qr_codes",
  "child_checkin",
  "bulk_email",
  "sacramental_records",
  "branches",
  "audit_logs",
  "users",
  "projects",
  "expenses",
  "payroll",
]);

// Cell Components with proper PropTypes
const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight="bold">
    {value}
  </Typography>
);
SerialCell.propTypes = {
  value: PropTypes.any.isRequired,
};

const MemberCell = ({ value, row }) => (
  <Box display="flex" alignItems="center" lineHeight={1}>
    <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{value?.charAt(0)}</Avatar>
    <Box ml={2} lineHeight={1}>
      <Typography display="block" variant="button" fontWeight="medium">
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {row.original.phone || "No phone"}
      </Typography>
    </Box>
  </Box>
);
MemberCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({
      phone: PropTypes.string,
    }),
  }).isRequired,
};

const ROLE_LABELS = {
  admin: "Admin",
  pastor: "Pastor",
  staff: "Staff",
  treasurer: "Treasurer",
  department_head: "Department Head",
  ministry_head: "Ministry Head",
  branch_admin: "Branch Admin",
  member: "Member",
};

const RoleCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight="bold">
    {ROLE_LABELS[value] || value}
  </Typography>
);
RoleCell.propTypes = {
  value: PropTypes.string,
};

const StatusCell = ({ value }) => (
  <Box ml={-1}>
    <StatusChip value={value} color={value === "active" ? "success" : "error"} />
  </Box>
);
StatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <Box display="flex" justifyContent="center" gap={1}>
    <Button
      variant="contained"
      color="info"
      size="small"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      variant="contained"
      color="error"
      size="small"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Box>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function useUserData() {
  const { user, church } = useAuth();

  const [members, setMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [userData, setUserData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "Member", accessor: "memberName", align: "left", width: "25%", Cell: MemberCell },
      { Header: "Username", accessor: "username", align: "center", width: "15%", Cell: SerialCell },
      { Header: "Email", accessor: "email", align: "center", width: "15%", Cell: SerialCell },
      { Header: "Role", accessor: "role", align: "center", width: "15%", Cell: RoleCell },
      { Header: "Status", accessor: "status", align: "center", width: "15%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const fetchMembers = useCallback(async () => {
    try {
      setIsLoadingMembers(true);
      const token = localStorage.getItem("authToken");
      if (!token || !church?.id) {
        setMembers([]);
        return;
      }

      const response = await fetch(`${API_BASE}/members/church/${church.id}`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch members");

      const data = await response.json();
      setMembers(data || []);
    } catch (error) {
      console.error("Error fetching members:", error);
      Swal.fire("Error", "Failed to load members", "error");
      setMembers([]);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [church?.id]);

  const handleMemberSelect = useCallback((member) => {
    setSelectedUser((prev) => ({
      ...prev,
      member_id: member.id,
      member_name: `${member.first_name || ""} ${member.surname || ""}`.trim(),
      email: member.email || prev?.email || "",
      phone: member.phone || prev?.phone || "",
    }));
  }, []);

  const handleEdit = useCallback(
    (user) => {
      setSelectedUser({
        ...user,
        member_name: user.memberName,
        confirmPassword: "",
      });
      fetchMembers();
      setEditModalOpen(true);
    },
    [fetchMembers]
  );

  const handleDelete = useCallback((user) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  }, []);

  const handleAddUser = useCallback(() => {
    setSelectedUser({
      id: null,
      member_id: null,
      member_name: "",
      username: "",
      name: "", // Added name field
      email: "",
      phone: "",
      role: "member",
      status: "active",
      permissions: [],
      password: "",
      confirmPassword: "",
    });
    fetchMembers();
    setEditModalOpen(true);
  }, [fetchMembers]);

  const fetchUsers = useCallback(async () => {
    try {
      setUserData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) {
        setUserData((prev) => ({ ...prev, isLoading: false, rows: [] }));
        return;
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        setUserData((prev) => ({ ...prev, isLoading: false, rows: [] }));
        return;
      }

      const response = await fetch(`${API_BASE}/users/church/${church.id}`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      console.log("API Response:", result); // Debug log

      const users = result.data || result.users || result || [];
      console.log("Processed users:", users);

      setUserData((prev) => ({
        ...prev,
        rows: users.map((user, index) => ({
          id: user.id,
          serial: index + 1,
          memberName:
            user.member?.first_name && user.member?.surname
              ? `${user.member.first_name} ${user.member.surname}`
              : user.member_name || "N/A",
          member_id: user.member_id ?? user.member?.id ?? null,
          username: user.name || user.username || "N/A",
          email: user.email,
          phone: user.phone || user.member?.phone || "",
          role: user.role,
          permissions: user.permissions || [],
          status: user.status || "active",
          createdAt: user.created_at || new Date().toISOString(),
          originalData: user,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setUserData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
        rows: [],
      }));
    }
  }, [user, church]);

  const handleEditSubmit = useCallback(async () => {
    try {
      if (!selectedUser) return;

      // Validation. A linked member is only required for roles that are
      // intrinsically tied to a congregation member — staff-type roles
      // (admin/pastor/staff/treasurer/branch_admin) can be plain logins.
      const ROLES_REQUIRING_MEMBER = ["member", "department_head", "ministry_head"];
      if (ROLES_REQUIRING_MEMBER.includes(selectedUser.role) && !selectedUser.member_id) {
        throw new Error("Please select a member");
      }
      if (!selectedUser.username && !selectedUser.name) {
        throw new Error("Username/Name is required");
      }
      if (!selectedUser.email) {
        throw new Error("Email is required");
      }
      if (!selectedUser?.id && (!selectedUser.password || selectedUser.password.length < 6)) {
        throw new Error("Password must be at least 6 characters");
      }
      if (selectedUser?.id && selectedUser.password && selectedUser.password.length < 6) {
        throw new Error("New password must be at least 6 characters");
      }
      if (selectedUser.password && selectedUser.password !== selectedUser.confirmPassword) {
        throw new Error("Passwords do not match");
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      if (!church?.id) {
        throw new Error("Church ID not found");
      }

      const url = selectedUser?.id ? `${API_BASE}/users/${selectedUser.id}` : `${API_BASE}/users`;
      const method = selectedUser?.id ? "PUT" : "POST";

      // Check what field your backend expects - 'name' or 'username'
      const payload = {
        member_id: selectedUser.member_id,
        name: selectedUser.username || selectedUser.name, // Try both
        username: selectedUser.username || selectedUser.name, // Try both
        email: selectedUser.email,
        phone: selectedUser.phone || "",
        role: selectedUser.role,
        status: selectedUser.status,
        permissions: (selectedUser.permissions || []).filter((p) => VALID_PERM_KEYS.has(p)),
        church_id: church.id,
        // When role is *_head, the backend links the user to this group.
        department_id:
          selectedUser.role === "department_head" ? selectedUser.department_id || null : null,
        ministry_id:
          selectedUser.role === "ministry_head" ? selectedUser.ministry_id || null : null,
        branch_id: selectedUser.role === "branch_admin" ? selectedUser.branch_id || null : null,
      };

      // Friendly validation for head roles — backend will reject silently otherwise.
      if (selectedUser.role === "department_head" && !payload.department_id) {
        throw new Error("Pick which department this person will head.");
      }
      if (selectedUser.role === "ministry_head" && !payload.ministry_id) {
        throw new Error("Pick which ministry this person will head.");
      }
      if (selectedUser.role === "branch_admin" && !payload.branch_id) {
        throw new Error("Pick which branch this person will manage.");
      }

      // Remove undefined fields
      Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);

      if (selectedUser.password) {
        payload.password = selectedUser.password;
      }

      console.log("Sending payload:", payload); // Debug log

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();
      console.log("API Response:", responseData); // Debug log

      if (!response.ok) {
        throw new Error(responseData.message || responseData.error || "Failed to save user");
      }

      Swal.fire({
        title: "Success!",
        text: `User ${selectedUser?.id ? "updated" : "created"} successfully`,
        icon: "success",
      });

      fetchUsers();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Submit failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
      });
    }
  }, [selectedUser, church?.id, fetchUsers]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token || !selectedUser) {
        throw new Error("Authentication token or selected user not found");
      }

      const response = await fetch(`${API_BASE}/users/${selectedUser.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();
      console.log("Delete response:", responseData); // Debug log

      if (!response.ok)
        throw new Error(responseData.message || `HTTP error! status: ${response.status}`);

      Swal.fire({
        title: "Success!",
        text: "User deleted successfully",
        icon: "success",
      });

      fetchUsers();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
      });
    }
  }, [selectedUser, fetchUsers]);

  useEffect(() => {
    if (church?.id) {
      fetchUsers();
    }
  }, [church?.id, fetchUsers]);

  return {
    ...userData,
    members: members || [],
    isLoadingMembers,
    handleMemberSelect,
    handleEdit,
    handleDelete,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedUser,
    setSelectedUser,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddUser,
  };
}
